/**
 * Optional runtime override for the backend base URL (no rebuild).
 * Place `runtime-config.json` in `public/` — it is copied to the web root at build time.
 * In native apps you can replace that single file under the packaged `www/` before `cap sync`.
 *
 * Schema: `{ "apiBaseUrl": "https://example.com" }`
 * If `apiBaseUrl` is empty or missing, `VITE_BACKEND_URL` (and dev default) apply.
 */

const DEFAULT_DEV = 'http://localhost:8000'

let runtimeBackendUrl: string | null = null

function trimBase(u: string): string {
  return u.replace(/\/+$/, '')
}

export function getBackendBaseUrl(): string {
  if (runtimeBackendUrl) return runtimeBackendUrl
  const env = import.meta.env.VITE_BACKEND_URL
  if (env && String(env).trim()) return trimBase(String(env))
  return DEFAULT_DEV
}

/**
 * Load override from `/runtime-config.json` (same origin as the app bundle).
 * Safe to call multiple times; first successful load wins.
 *
 * Skipped in Vite `npm run dev` so `VITE_BACKEND_URL` from `.env.development` (e.g. localhost:8000)
 * is not overridden by a committed `public/runtime-config.json` meant for production.
 */
export async function initRuntimeApiConfig(): Promise<void> {
  if (runtimeBackendUrl) return
  if (import.meta.env.DEV) return
  try {
    const res = await fetch('/runtime-config.json', { cache: 'no-store' })
    if (!res.ok) return
    const data: unknown = await res.json()
    if (!data || typeof data !== 'object') return
    const raw = (data as { apiBaseUrl?: unknown }).apiBaseUrl
    if (typeof raw !== 'string') return
    const trimmed = raw.trim()
    if (!trimmed) return
    if (!/^https?:\/\//i.test(trimmed)) return
    try {
      new URL(trimmed)
    } catch {
      return
    }
    runtimeBackendUrl = trimBase(trimmed)
  } catch {
    // keep Vite env / default
  }
}
